(() => {
  'use strict';

  const one = (root, selector) => root.querySelector(selector);
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const money = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((Number(cents) || 0) / 100);
  const normalize = (value) => String(value || '').replace(/[“”″]/g, '"').replace(/[‘’′]/g, "'").trim().toLowerCase();
  const FONT_STACKS = Object.freeze({
    arialBlack: "'FSP Arial Black', 'Arial Black', Arial, sans-serif",
    impact: "'FSP Impact', Impact, 'Arial Narrow', sans-serif"
  });
  const normalizeFontStack = (value) => {
    const font = String(value || '').trim();
    if (!font) return 'Arial, Helvetica, sans-serif';
    const comparable = font.replace(/["']/g, '').replace(/\s+/g, ' ').toLowerCase();
    if (comparable.startsWith('fsp arial black') || comparable.startsWith('arial black')) return FONT_STACKS.arialBlack;
    if (comparable.startsWith('fsp impact') || comparable.startsWith('impact')) return FONT_STACKS.impact;
    return font;
  };
  const ORACAL_COLOR_NAMES = Object.freeze({
    '010': 'White', '070': 'Black', '021': 'Yellow', '031': 'Red',
    '312': 'Burgundy', '034': 'Orange', '404': 'Purple', '041': 'Pink',
    '086': 'Brilliant Blue', '061': 'Green', '080': 'Brown', '071': 'Gray'
  });
  const swatchName = (option) => ORACAL_COLOR_NAMES[option?.dataset.code] || option?.textContent.trim() || '';

  function parseJSON(root, selector, fallback) {
    try { return JSON.parse(one(root, selector)?.textContent || ''); } catch (_) { return fallback; }
  }

  function toast(message) {
    const el = document.querySelector('[data-fsp-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._timer);
    el._timer = setTimeout(() => { el.hidden = true; }, 2600);
  }

  document.querySelectorAll('[data-fsp-lab]').forEach(initDesigner);

  function initDesigner(lab) {
    const type = lab.dataset.designer;
    const stickerShape = lab.dataset.stickerShape || 'circle';
    const product = parseJSON(lab, '[data-main-product-json]', null);
    const accessories = parseJSON(lab, '[data-accessory-product-json]', {});
    const sign = one(lab, '[data-sign]');
    const safeArea = one(lab, '[data-safe-area]');
    const stage = one(lab, '[data-stage]');
    const addButton = one(lab, '[data-add-design-to-cart]');
    const cartMessage = one(lab, '[data-cart-message]');
    const selectedEditor = one(lab, '[data-selected-editor]');
    const selectedText = one(lab, '[data-selected-text]');
    const fontSelect = one(lab, '[data-font]');
    const colorSelect = one(lab, '[data-color]');
    const outlineColorSelect = one(lab, '[data-outline-color]');
    const outlineThicknessInput = one(lab, '[data-outline-thickness]');
    const outlineThicknessValue = one(lab, '[data-outline-thickness-value]');
    const backgroundColorSelect = one(lab, '[data-background-color]');
    const fontReadout = one(lab, '[data-font-readout]');
    const sizeSelect = one(lab, '[data-option="Size"]');
    const orientationSelect = one(lab, '[data-pipeline-orientation]');
    const materialSelect = one(lab, '[data-option="Material"]') || one(lab, '[data-option="Material & Finish"]');
    const packQuantitySelect = one(lab, '[data-option="Pack Quantity"]');
    const mountingSelect = one(lab, '[data-option="Mounting"]');
    const artworkInput = one(lab, '[data-artwork-input]');
    const removeArtworkButton = one(lab, '[data-remove-artwork]');
    const hardhatOutlineControls = one(lab, '.hardhat-outline-controls');
    if (type === 'hardhat' && selectedEditor && hardhatOutlineControls) {
      selectedEditor.insertBefore(hardhatOutlineControls, one(selectedEditor, '.editor-actions'));
    }
    if ((type === 'banner' || type === 'hardhat') && selectedEditor) {
      const editorActions = one(selectedEditor, '.editor-actions');
      if (editorActions && !one(editorActions, '[data-fit-artwork]')) {
        const fitButton = document.createElement('button');
        fitButton.type = 'button';
        fitButton.dataset.fitArtwork = '';
        fitButton.textContent = 'Fit artwork';
        fitButton.hidden = true;
        editorActions.prepend(fitButton);
      }
    }
    const history = [];
    const future = [];
    let selectedLayer = null;
    let keyCounter = 3;
    let drag = null;
    let resize = null;
    let rotation = null;
    let lastTap = { time: 0, layer: null };
    let colorPicker = null;
    let colorTrigger = null;
    let colorMenu = null;
    let outlineColorPicker = null;
    let outlineColorTrigger = null;
    let outlineColorMenu = null;
    const mobileQuery = window.matchMedia('(max-width: 900px)');

    if (type === 'rider' && colorSelect) {
      colorSelect.innerHTML = '<option value="#b0000d" data-code="031">031</option><option value="#0d0e11" data-code="070">070</option>';
    }

    const hardhatSizes = stickerShape === 'oval'
      ? ['2.5″ × 1.5″', '2″ × 1.25″', '3″ × 2″']
      : stickerShape === 'die-cut'
        ? ['Up to 2″ × 2″', 'Up to 2.5″ × 2.5″', 'Up to 3″ × 3″']
        : ['2″ × 2″', '1.5″ × 1.5″', '2.5″ × 2.5″', '3″ × 3″'];

    const variantOptionValues = (index) => Array.from(new Set((product?.variants || []).map((variant) => variant.options?.[index] || variant[`option${index + 1}`]).filter(Boolean)));
    const defaultSizeOptions = type === 'safety'
      ? ['14″ × 10″', '10″ × 7″', '18″ × 12″', '24″ × 18″']
      : type === 'pipeline'
        ? ['14″ × 10″', '12″ × 18″']
        : type === 'banner'
          ? ['24″ × 36″', '24″ × 48″', '24″ × 72″', '36″ × 48″', '36″ × 60″', '36″ × 72″', '36″ × 96″', '36″ × 120″', '48″ × 72″', '48″ × 96″', '48″ × 120″']
          : type === 'hardhat'
            ? hardhatSizes
            : type === 'custom'
            ? ['18″ × 24″ Landscape', '18″ × 24″ Portrait', '10″ × 14″ Landscape', '10″ × 14″ Portrait', '12″ × 18″ Landscape', '12″ × 18″ Portrait', '24″ × 36″ Landscape', '24″ × 36″ Portrait']
            : [];
    const productSizeOptions = ['safety', 'pipeline', 'banner', 'hardhat', 'custom'].includes(type) ? variantOptionValues(0) : [];
    const sizeOptions = productSizeOptions.length ? productSizeOptions : defaultSizeOptions;

    if (sizeSelect) {
      sizeSelect.innerHTML = sizeOptions.map((label) => `<option>${label}</option>`).join('');
    }
    if (materialSelect) {
      const materialOptions = variantOptionValues(1);
      if (materialOptions.length) materialSelect.innerHTML = materialOptions.map((label) => `<option>${label}</option>`).join('');
    }
    if (packQuantitySelect) {
      const packOptions = variantOptionValues(2);
      if (packOptions.length) packQuantitySelect.innerHTML = packOptions.map((label) => `<option>${label}</option>`).join('');
    }

    const signalSelect = one(lab, '[data-signal-word]');
    const signalHeader = one(lab, '[data-safety-header]');
    if (signalSelect && signalHeader) {
      signalSelect.value = signalHeader.dataset.signalValue || 'DANGER';
      sign.dataset.signal = signalSelect.value.toLowerCase();
    }

    all(lab, '[data-bind]').forEach((input) => {
      const layer = layerByKey(input.dataset.bind);
      if (layer && !input.value) input.value = one(layer, '[data-layer-content]')?.textContent || '';
    });

    function layerByKey(key) { return one(safeArea, `[data-layer][data-key="${CSS.escape(key)}"]`); }
    function layers() { return all(safeArea, '[data-layer]'); }

    function layerState(layer) {
      return {
        key: layer.dataset.key,
        kind: layer.dataset.kind,
        classes: Array.from(layer.classList).filter((name) => name !== 'selected' && name !== 'editing').join(' '),
        html: layer.dataset.plainText || one(layer, '[data-layer-content]')?.textContent || '',
        src: one(layer, 'img')?.src || '',
        hidden: layer.hidden,
        lockedLayout: layer.dataset.lockedLayout || '',
        x: Number(layer.dataset.x || 50), y: Number(layer.dataset.y || 50),
        rotation: Number(layer.dataset.rotation || 0),
        fontSize: Number(layer.dataset.fontSize || 64), size: Number(layer.dataset.size || 35),
        font: normalizeFontStack(layer.dataset.font),
        color: layer.dataset.color || '#0d0e11',
        colorCode: layer.dataset.colorCode || '070',
        outlineColor: layer.dataset.outlineColor || '#e6e9ee',
        outlineColorCode: layer.dataset.outlineColorCode || '010',
        outlineThickness: Number(layer.dataset.outlineThickness || 0),
        textShape: layer.dataset.textShape || 'straight'
      };
    }

    function capture() { return layers().map(layerState); }
    function pushHistory() {
      history.push(capture());
      if (history.length > 40) history.shift();
      future.length = 0;
      updateHistoryButtons();
    }

    function restore(state) {
      safeArea.querySelectorAll('[data-layer]').forEach((layer) => layer.remove());
      state.forEach((item) => safeArea.appendChild(makeLayer(item)));
      bindLayers();
      selectedLayer = null;
      const lastVisible = state.slice().reverse().find((item) => !item.hidden);
      selectLayer(lastVisible ? layerByKey(lastVisible.key) : null);
      fitAll();
      renderLayerPanel();
    }

    function updateHistoryButtons() {
      const undo = one(lab, '[data-undo]'); const redo = one(lab, '[data-redo]');
      if (undo) undo.disabled = history.length === 0;
      if (redo) redo.disabled = future.length === 0;
    }

    function undo() {
      if (!history.length) return;
      future.push(capture());
      restore(history.pop());
      updateHistoryButtons();
      toast('Change undone');
    }

    function redo() {
      if (!future.length) return;
      history.push(capture());
      restore(future.pop());
      updateHistoryButtons();
      toast('Change restored');
    }

    function handles() { return '<i class="resize-handle nw"></i><i class="resize-handle ne"></i><i class="resize-handle sw"></i><i class="resize-handle se"></i><i class="rotate-handle" role="button" aria-label="Rotate item" title="Rotate item">↻</i>'; }
    function makeLayer(item) {
      const layer = document.createElement('div');
      layer.className = item.classes || `design-layer ${item.kind === 'image' ? 'image-layer' : type + '-layer'} dynamic-layer`;
      layer.dataset.layer = '';
      Object.assign(layer.dataset, {
        key: item.key, kind: item.kind, x: item.x, y: item.y, rotation: item.rotation || 0,
        lockedLayout: item.lockedLayout || '',
        fontSize: item.fontSize || 64, size: item.size || 35,
        font: normalizeFontStack(item.font), color: item.color || '#0d0e11', colorCode: item.colorCode || '070',
        outlineColor: item.outlineColor || '#e6e9ee', outlineColorCode: item.outlineColorCode || '010', outlineThickness: item.outlineThickness || 0,
        textShape: item.textShape || 'straight', plainText: item.html || 'NEW TEXT'
      });
      if (item.kind === 'image') {
        layer.innerHTML = `<img data-artwork-preview alt="Uploaded artwork">${handles()}`;
        if (item.src) one(layer, 'img').src = item.src;
      } else {
        layer.innerHTML = `<span data-layer-content>${escapeHTML(item.html || 'NEW TEXT')}</span>${handles()}`;
      }
      layer.hidden = Boolean(item.hidden);
      applyLayerStyle(layer);
      return layer;
    }

    function escapeHTML(value) {
      const div = document.createElement('div'); div.textContent = value; return div.innerHTML;
    }

    function plainLayerText(layer) {
      if (!layer) return '';
      const content = one(layer, '[data-layer-content]');
      return layer.dataset.plainText ?? content?.textContent ?? '';
    }

    function applyTextShape(layer) {
      if (!['custom', 'banner', 'hardhat'].includes(type) || !layer || layer.dataset.kind !== 'text') return;
      const content = one(layer, '[data-layer-content]');
      if (!content || content.isContentEditable) return;
      const text = plainLayerText(layer);
      const shape = layer.dataset.textShape || 'straight';
      layer.dataset.plainText = text;
      content.className = '';
      content.dataset.layerContent = '';
      content.replaceChildren();
      if (shape === 'straight' || text.length < 2) {
        content.textContent = text;
        content.style.paddingBlock = '';
        return;
      }
      content.classList.add('text-shaped', `text-shaped--${shape}`);
      content.style.paddingBlock = '.48em';
      const characters = Array.from(text);
      characters.forEach((character) => {
        const span = document.createElement('span');
        span.className = 'shaped-char';
        span.textContent = character === ' ' ? '\u00a0' : character;
        content.appendChild(span);
      });
      const characterElements = Array.from(content.children);
      const measuredWidths = characterElements.map((span) => Math.max(1, span.getBoundingClientRect().width));
      const measuredTotal = measuredWidths.reduce((sum, width) => sum + width, 0);
      const useMeasuredWidths = measuredTotal > characterElements.length;
      let cursor = 0;
      const centers = measuredWidths.map((measuredWidth) => {
        const width = useMeasuredWidths ? measuredWidth : 1;
        const center = cursor + width / 2;
        cursor += width;
        return center;
      });
      const firstCenter = centers[0] || 0;
      const lastCenter = centers[centers.length - 1] || firstCenter;
      characterElements.forEach((span, index) => {
        const normalized = lastCenter > firstCenter
          ? (((centers[index] - firstCenter) / (lastCenter - firstCenter)) * 2) - 1
          : 0;
        let y = 0;
        let rotate = 0;
        if (shape === 'arch-up') {
          y = -0.3 * (1 - normalized * normalized);
        } else if (shape === 'arch-down') {
          y = 0.3 * (1 - normalized * normalized);
        } else if (shape === 'wave') {
          const progress = (normalized + 1) / 2;
          y = Math.sin(progress * Math.PI * 2) * 0.18;
          rotate = Math.cos(progress * Math.PI * 2) * 4;
        }
        span.style.setProperty('--shape-y', `${y.toFixed(3)}em`);
        span.style.setProperty('--shape-rotate', `${rotate.toFixed(2)}deg`);
      });
    }

    function applyLayerStyle(layer) {
      layer.style.left = `${Number(layer.dataset.x || 50)}%`;
      layer.style.top = `${Number(layer.dataset.y || 50)}%`;
      layer.style.transform = `translate(-50%, -50%) rotate(${Number(layer.dataset.rotation || 0)}deg)`;
      const normalizedFont = normalizeFontStack(layer.dataset.font);
      layer.dataset.font = normalizedFont;
      layer.style.fontFamily = normalizedFont;
      layer.style.color = layer.dataset.color || '#0d0e11';
      if (layer.dataset.kind === 'text') {
        const dims = currentDimensions();
        const printableWidth = Math.max(dims.width - horizontalInset() * 2, 1);
        const previewWidth = safeArea.clientWidth;
        layer.style.fontSize = previewWidth
          ? `${((Number(layer.dataset.fontSize || 64) / 72) / printableWidth) * previewWidth}px`
          : `${((Number(layer.dataset.fontSize || 64) / 72) / printableWidth) * 100}cqw`;
        const content = one(layer, '[data-layer-content]');
        const outlineThickness = type === 'custom' || type === 'banner' || type === 'hardhat' ? clamp(Number(layer.dataset.outlineThickness || 0), 0, 12) : 0;
        const outlineWidth = previewWidth ? ((outlineThickness / 72) / printableWidth) * previewWidth : 0;
        if (content) {
          content.style.webkitTextStroke = outlineThickness > 0 ? `${Math.max(.25, outlineWidth)}px ${layer.dataset.outlineColor || '#e6e9ee'}` : '0 transparent';
          content.style.paintOrder = 'stroke fill';
          applyTextShape(layer);
        }
      } else {
        const requestedSize = Number(layer.dataset.size || 35);
        const maximumSize = imageSizeLimit(layer);
        const fittedSize = clamp(requestedSize, Math.min(8, maximumSize), maximumSize);
        layer.dataset.size = fittedSize.toFixed(1);
        layer.style.width = `${fittedSize}%`;
      }
      fitLayer(layer);
    }

    function imageSizeLimit(layer) {
      if (!layer || layer.dataset.kind !== 'image' || !safeArea.clientWidth || !safeArea.clientHeight) return 95;
      const image = one(layer, 'img');
      if (!image || !image.naturalWidth || !image.naturalHeight || image.hidden) return 95;
      const edgeRoom = type === 'banner' ? 4 : 2;
      const availableWidth = Math.max(1, safeArea.clientWidth - edgeRoom * 2);
      const availableHeight = Math.max(1, safeArea.clientHeight - edgeRoom * 2);
      const imageRatio = image.naturalWidth / image.naturalHeight;
      const widthThatFitsHeight = availableHeight * imageRatio;
      return clamp((Math.min(availableWidth, widthThatFitsHeight) / safeArea.clientWidth) * 100, 3, 95);
    }

    function fitArtworkLayer(layer, announce = false) {
      if (!layer || layer.hidden || layer.dataset.kind !== 'image') return;
      layer.dataset.size = imageSizeLimit(layer).toFixed(1);
      layer.dataset.x = '50';
      layer.dataset.y = '50';
      applyLayerStyle(layer);
      constrainLayer(layer);
      updateSelectedReadout();
      if (announce) toast(type === 'hardhat' ? 'Artwork fitted inside the sticker' : 'Artwork fitted inside the banner');
    }

    function horizontalInset() { return type === 'lease' ? 1 : type === 'banner' ? 1.5 : type === 'hardhat' ? 0.125 : 0.75; }

    function applyBackgroundColor() {
      if (!backgroundColorSelect || (type !== 'custom' && type !== 'banner' && type !== 'hardhat')) return;
      sign.style.backgroundColor = backgroundColorSelect.value || '#ffffff';
      const toolbarSwatch = one(lab, '[data-toolbar-background-swatch]');
      if (toolbarSwatch) toolbarSwatch.style.setProperty('--toolbar-background', backgroundColorSelect.value || '#ffffff');
    }

    function currentDimensions() {
      if (type === 'lease') return { width: 24, height: 18, label: '24″ × 18″' };
      if (type === 'rider') return { width: 24, height: 6, label: '24″ × 6″' };
      const value = sizeSelect?.value || (type === 'custom' ? '18″ × 24″ Landscape' : type === 'banner' ? '24″ × 36″' : type === 'hardhat' ? '2″ × 2″' : '14″ × 10″');
      const nums = value.match(/[\d.]+/g)?.slice(0, 2).map(Number) || [14, 10];
      if (type === 'pipeline') {
        const portrait = orientationSelect?.value === 'Portrait';
        const long = Math.max(...nums);
        const short = Math.min(...nums);
        return { width: portrait ? short : long, height: portrait ? long : short, label: `${portrait ? short : long}″ × ${portrait ? long : short}″` };
      }
      if (type === 'custom') {
        const portrait = value.includes('Portrait');
        return { width: portrait ? Math.min(...nums) : Math.max(...nums), height: portrait ? Math.max(...nums) : Math.min(...nums), label: value };
      }
      if (type === 'banner') return { width: Math.max(...nums), height: Math.min(...nums), label: value };
      if (type === 'hardhat') return { width: nums[0], height: nums[1] || nums[0], label: value };
      return { width: nums[0], height: nums[1], label: value };
    }

    function applyDimensions() {
      const dims = currentDimensions();
      const contentInset = horizontalInset();
      const headerOutset = Math.max(contentInset - 0.5, 0);
      const printableWidth = Math.max(dims.width - contentInset * 2, 1);
      const printableHeight = Math.max(dims.height - contentInset * 2, 1);
      sign.style.aspectRatio = `${dims.width} / ${dims.height}`;
      sign.style.setProperty('--sign-width', dims.width);
      sign.style.setProperty('--sign-height', dims.height);
      sign.style.setProperty('--safe-x', `${(contentInset / dims.width) * 100}%`);
      sign.style.setProperty('--safe-y', `${(contentInset / dims.height) * 100}%`);
      sign.style.setProperty('--header-outset-x', `${(headerOutset / printableWidth) * 100}%`);
      sign.style.setProperty('--header-outset-y', `${(headerOutset / printableHeight) * 100}%`);
      sign.className = `fsp-sign fsp-sign--${type}${type === 'hardhat' ? ` hardhat-shape--${stickerShape}` : ''}${type === 'pipeline' && orientationSelect?.value === 'Portrait' ? ' is-portrait' : ''}`;
      sizeSignToStage(dims);
      const readout = one(lab, '[data-size-readout]'); if (readout) readout.textContent = `${dims.width}″ wide × ${dims.height}″ high`;
      layers().forEach(applyLayerStyle);
      renderMountingHoles();
      updatePrice();
    }

    function sizeSignToStage(dims) {
      if (!stage.clientWidth || !stage.clientHeight) return;
      const stageStyle = getComputedStyle(stage);
      const horizontalPadding = parseFloat(stageStyle.paddingLeft || 0) + parseFloat(stageStyle.paddingRight || 0);
      const verticalPadding = parseFloat(stageStyle.paddingTop || 0) + parseFloat(stageStyle.paddingBottom || 0);
      const availableWidth = Math.max(100, (stage.clientWidth - horizontalPadding) * 0.96);
      const availableHeight = Math.max(80, (stage.clientHeight - verticalPadding) * 0.96);
      const scale = Math.min(availableWidth / dims.width, availableHeight / dims.height);
      sign.style.width = `${Math.max(1, Math.floor(dims.width * scale))}px`;
      sign.style.height = `${Math.max(1, Math.floor(dims.height * scale))}px`;
      sign.style.maxHeight = 'none';
    }

    function fitLayer(layer) {
      if (!layer || layer.dataset.kind !== 'text' || layer.hidden) return;
      if (layer.dataset.key === 'warning') { fitPipelineWarning(); return; }
      const content = one(layer, '[data-layer-content]');
      if (!content || !safeArea.clientWidth) return;
      content.style.position = 'static';
      content.style.left = '';
      content.style.top = '';
      content.style.transform = 'none';
      layer.style.width = 'max-content';
      layer.style.height = 'auto';
      const available = Math.max(20, safeArea.clientWidth - 6);
      const natural = Math.max(1, content.scrollWidth);
      const naturalHeight = Math.max(1, content.getBoundingClientRect().height);
      const fittedWidth = Math.min(natural, available);
      layer.style.width = `${fittedWidth}px`;
      layer.style.height = `${naturalHeight}px`;
      content.style.position = 'absolute';
      content.style.left = '50%';
      content.style.top = '50%';
      content.style.transformOrigin = 'center center';
      const widthScale = Math.min(1, fittedWidth / natural);
      const heightScale = Math.min(1, Math.max(20, safeArea.clientHeight - 6) / naturalHeight);
      const fitScale = Math.min(widthScale, heightScale);
      content.style.transform = type === 'hardhat'
        ? `translate(-50%, -50%) scale(${fitScale})`
        : `translate(-50%, -50%) scaleX(${widthScale})`;
    }

    function constrainLayer(layer) {
      if (!layer || layer.hidden || !safeArea.clientWidth || !safeArea.clientHeight) return;
      if (layer.dataset.lockedLayout === 'true') return;
      const safeRect = safeArea.getBoundingClientRect();
      const layerRect = layer.getBoundingClientRect();
      const halfWidth = Math.min(49, (layerRect.width / safeRect.width) * 50);
      const halfHeight = Math.min(49, (layerRect.height / safeRect.height) * 50);
      const x = clamp(Number(layer.dataset.x || 50), halfWidth, 100 - halfWidth);
      const y = clamp(Number(layer.dataset.y || 50), halfHeight, 100 - halfHeight);
      layer.dataset.x = x.toFixed(2);
      layer.dataset.y = y.toFixed(2);
      layer.style.left = `${x}%`;
      layer.style.top = `${y}%`;
    }

    function fitPipelineWarning() {
      const header = one(safeArea, '.pipeline-warning');
      const label = header && one(header, 'span');
      if (!header || !label || !header.clientWidth || !header.clientHeight) return;
      label.style.transform = 'translate(-50%, -50%)';
      const style = getComputedStyle(header);
      const availableWidth = Math.max(1, header.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0));
      const availableHeight = Math.max(1, header.clientHeight);
      const rect = label.getBoundingClientRect();
      const naturalWidth = Math.max(1, rect.width);
      const naturalHeight = Math.max(1, rect.height);
      const scaleX = clamp((availableWidth / naturalWidth) * 0.99, 0.65, 1.45);
      const scaleY = clamp((availableHeight / naturalHeight) * 0.96, 0.65, 1.45);
      label.style.transformOrigin = 'center center';
      label.style.transform = `translate(-50%, -50%) scale(${scaleX}, ${scaleY})`;
    }

    function fitAll() {
      layers().forEach((layer) => { fitLayer(layer); constrainLayer(layer); });
      fitPipelineWarning();
    }

    function applyPipelineOrientationLayout() {
      if (type !== 'pipeline') return;
      const portrait = orientationSelect?.value === 'Portrait';
      const positions = portrait
        ? { pipeline: [39, 140], 'before-digging': [53, 43], 'emergency-call': [59, 43], phone: [73, 84], company: [91, 50] }
        : { pipeline: [43, 151], 'before-digging': [59, 47], 'emergency-call': [65, 47], phone: [78, 94], company: [94, 52] };
      Object.entries(positions).forEach(([key, values]) => {
        const layer = layerByKey(key);
        if (!layer) return;
        layer.dataset.y = String(values[0]);
        layer.dataset.fontSize = String(values[1]);
      });
    }

    function selectLayer(layer) {
      layers().forEach((item) => item.classList.toggle('selected', item === layer));
      selectedLayer = layer;
      if (!selectedEditor) return;
      selectedEditor.hidden = !layer;
      if (!layer) { renderLayerPanel(); return; }
      const isText = layer.dataset.kind === 'text';
      const fitArtworkButton = one(selectedEditor, '[data-fit-artwork]');
      const content = one(layer, '[data-layer-content]');
      one(selectedEditor, '[data-text-editor-row]').hidden = !isText;
      one(selectedEditor, '[data-font-row]').hidden = !isText || type === 'rider';
      one(selectedEditor, '[data-color-row]').hidden = !isText;
      const outlineColorRow = one(selectedEditor, '[data-outline-color-row]');
      const outlineThicknessRow = one(selectedEditor, '[data-outline-thickness-row]');
      const textShapeRow = one(selectedEditor, '[data-text-shape-row]');
      if (outlineColorRow) outlineColorRow.hidden = !isText;
      if (outlineThicknessRow) outlineThicknessRow.hidden = !isText;
      if (textShapeRow) textShapeRow.hidden = !isText;
      if (fitArtworkButton) fitArtworkButton.hidden = isText || (type !== 'banner' && type !== 'hardhat');
      const editorActions = one(selectedEditor, '.editor-actions');
      if (editorActions) editorActions.hidden = layer.dataset.lockedLayout === 'true';
      if (isText) {
        selectedText.value = plainLayerText(layer);
        const normalizedFont = normalizeFontStack(layer.dataset.font);
        layer.dataset.font = normalizedFont;
        fontSelect.value = Array.from(fontSelect.options).some((option) => option.value === normalizedFont)
          ? normalizedFont
          : fontSelect.options[0].value;
        colorSelect.value = layer.dataset.color || '#0d0e11';
        if (outlineColorSelect) outlineColorSelect.value = layer.dataset.outlineColor || '#e6e9ee';
        if (outlineThicknessInput) {
          outlineThicknessInput.value = String(clamp(Number(layer.dataset.outlineThickness || 0), 0, 12));
          if (outlineThicknessValue) outlineThicknessValue.textContent = `${outlineThicknessInput.value} pt`;
        }
        refreshVisualSelect(fontSelect);
        updateSwatchSelect();
        updateOutlineSwatchSelect();
        all(selectedEditor, '[data-text-shape]').forEach((button) => {
          const active = button.dataset.textShape === (layer.dataset.textShape || 'straight');
          button.classList.toggle('active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      }
      updateSelectedReadout();
      renderLayerPanel();
    }

    function updateSelectedReadout() {
      if (!fontReadout || !selectedLayer) return;
      const angle = Math.round(Number(selectedLayer.dataset.rotation || 0));
      if (selectedLayer.dataset.kind === 'text') {
        const outline = type === 'custom' || type === 'banner' || type === 'hardhat' ? Number(selectedLayer.dataset.outlineThickness || 0) : 0;
        fontReadout.textContent = `${Math.round(Number(selectedLayer.dataset.fontSize || 64))} pt · ${angle}° · Maximum 400 pt${outline > 0 ? ` · ${outline} pt outline` : ''}`;
      } else {
        fontReadout.textContent = `${Math.round(Number(selectedLayer.dataset.size || 35))}% of sign width · ${angle}°`;
      }
    }

    function editLayer(layer) {
      if (!layer || layer.dataset.kind !== 'text') return;
      selectLayer(layer);
      if (mobileQuery.matches) {
        setPanel('design');
        beginMobileTextEditing(selectedText);
        return;
      }
      const content = one(layer, '[data-layer-content]');
      if (!content || content.isContentEditable) return;
      pushHistory();
      content.textContent = plainLayerText(layer);
      content.contentEditable = 'true';
      content.classList.add('editing');
      content.focus();
      const range = document.createRange(); range.selectNodeContents(content);
      const selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(range);
      const finish = () => {
        content.contentEditable = 'false'; content.classList.remove('editing');
        layer.dataset.plainText = content.textContent;
        const input = one(lab, `[data-bind="${CSS.escape(layer.dataset.key)}"]`);
        if (input) input.value = content.textContent;
        if (selectedText) selectedText.value = content.textContent;
        applyTextShape(layer);
        fitLayer(layer); renderLayerPanel();
      };
      content.addEventListener('blur', finish, { once: true });
      content.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === 'Escape') { event.preventDefault(); content.blur(); } });
    }

    function bindLayers() {
      layers().forEach((layer) => {
        if (layer.dataset.lockedLayout !== 'true' && !one(layer, '.rotate-handle')) {
          const rotateHandle = document.createElement('i');
          rotateHandle.className = 'rotate-handle';
          rotateHandle.textContent = '↻';
          rotateHandle.title = 'Rotate item';
          rotateHandle.setAttribute('role', 'button');
          rotateHandle.setAttribute('aria-label', 'Rotate item');
          layer.appendChild(rotateHandle);
        }
        applyLayerStyle(layer);
        ensureCustomSignHandles(layer);
        layer.onpointerdown = startDrag;
        layer.ondblclick = (event) => { event.preventDefault(); event.stopPropagation(); editLayer(layer); };
        all(layer, '.resize-handle').forEach((handle) => { handle.onpointerdown = startResize; });
        const rotateHandle = one(layer, '.rotate-handle');
        if (rotateHandle) rotateHandle.onpointerdown = startRotate;
      });
    }

    function ensureCustomSignHandles(layer) {
      if (layer.dataset.lockedLayout === 'true' || one(layer, '.custom-sign-action')) return;
      const actions = [
        ['delete', '×', 'Delete item'],
        ['rotate', '↻', 'Rotate item'],
        ['duplicate', '▣', 'Duplicate item'],
        ['resize', '↘', 'Resize item']
      ];
      actions.forEach(([action, glyph, label]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `custom-sign-action custom-sign-action--${action}${action === 'resize' ? ' se' : ''}`;
        button.dataset.canvasAction = action;
        button.textContent = glyph;
        button.title = label;
        button.setAttribute('aria-label', label);
        if (action === 'rotate') button.onpointerdown = startRotate;
        else if (action === 'resize') button.onpointerdown = startResize;
        else button.addEventListener('pointerdown', (event) => { event.preventDefault(); event.stopPropagation(); });
        if (action === 'delete') button.addEventListener('click', () => { selectLayer(layer); deleteSelected(); });
        if (action === 'duplicate') button.addEventListener('click', () => { selectLayer(layer); duplicateSelected(); });
        layer.appendChild(button);
      });
    }

    function startDrag(event) {
      const layer = event.currentTarget;
      if (event.target.closest('.resize-handle, .rotate-handle, .custom-sign-action') || event.target.isContentEditable) return;
      if (layer.dataset.lockedLayout === 'true') { event.preventDefault(); selectLayer(layer); return; }
      event.preventDefault(); selectLayer(layer);
      const rect = safeArea.getBoundingClientRect();
      drag = { layer, pointerId: event.pointerId, rect, startX: event.clientX, startY: event.clientY, moved: false, historyPushed: false, pointerType: event.pointerType };
      layer.setPointerCapture?.(event.pointerId);
      layer.onpointermove = moveDrag;
      layer.onpointerup = endDrag;
      layer.onpointercancel = endDrag;
      layer.onlostpointercapture = endDrag;
    }

    function moveDrag(event) {
      if (!drag || drag.layer !== event.currentTarget) return;
      if (!drag.moved && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5) return;
      if (!drag.historyPushed) { pushHistory(); drag.historyPushed = true; }
      drag.moved = true;
      const x = clamp(((event.clientX - drag.rect.left) / drag.rect.width) * 100, 0, 100);
      const y = clamp(((event.clientY - drag.rect.top) / drag.rect.height) * 100, 0, 100);
      drag.layer.dataset.x = x.toFixed(2); drag.layer.dataset.y = y.toFixed(2); applyLayerStyle(drag.layer); constrainLayer(drag.layer);
    }

    function endDrag(event) {
      if (!drag) return;
      const completed = drag;
      completed.layer.onpointermove = null; completed.layer.onpointerup = null; completed.layer.onpointercancel = null; completed.layer.onlostpointercapture = null; drag = null;
      if (!completed.moved && event.type === 'pointerup') {
        const now = performance.now();
        if (lastTap.layer === completed.layer && now - lastTap.time < 480) {
          lastTap = { time: 0, layer: null };
          editLayer(completed.layer);
        } else {
          lastTap = { time: now, layer: completed.layer };
        }
      } else if (completed.moved) lastTap = { time: 0, layer: null };
      renderLayerPanel();
    }

    function startResize(event) {
      event.preventDefault(); event.stopPropagation();
      const handle = event.currentTarget;
      const layer = handle.closest('[data-layer]'); selectLayer(layer); pushHistory();
      const east = handle.classList.contains('ne') || handle.classList.contains('se');
      const south = handle.classList.contains('se') || handle.classList.contains('sw');
      resize = { handle, layer, startX: event.clientX, startY: event.clientY, startFont: Number(layer.dataset.fontSize || 64), startSize: Number(layer.dataset.size || 35), sx: east ? 1 : -1, sy: south ? 1 : -1 };
      handle.setPointerCapture?.(event.pointerId);
      handle.onpointermove = moveResize; handle.onpointerup = endResize; handle.onpointercancel = endResize;
    }

    function moveResize(event) {
      if (!resize || resize.handle !== event.currentTarget) return;
      event.preventDefault();
      const delta = ((event.clientX - resize.startX) * resize.sx + (event.clientY - resize.startY) * resize.sy) / 2;
      if (resize.layer.dataset.kind === 'text') resize.layer.dataset.fontSize = Math.round(clamp(resize.startFont + delta * 0.8, 18, 400));
      else {
        const maximumSize = imageSizeLimit(resize.layer);
        resize.layer.dataset.size = clamp(resize.startSize + delta * 0.18, Math.min(8, maximumSize), maximumSize).toFixed(1);
      }
      applyLayerStyle(resize.layer); constrainLayer(resize.layer); updateSelectedReadout();
    }

    function endResize(event) {
      if (!resize) return;
      resize.handle.onpointermove = null; resize.handle.onpointerup = null; resize.handle.onpointercancel = null; resize = null;
      constrainLayer(selectedLayer); selectLayer(selectedLayer); fitAll();
    }

    function startRotate(event) {
      event.preventDefault(); event.stopPropagation();
      const handle = event.currentTarget;
      const layer = handle.closest('[data-layer]');
      if (!layer) return;
      selectLayer(layer); pushHistory();
      const rect = layer.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      rotation = {
        handle,
        layer,
        centerX,
        centerY,
        startAngle: Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180 / Math.PI,
        startRotation: Number(layer.dataset.rotation || 0)
      };
      handle.setPointerCapture?.(event.pointerId);
      handle.onpointermove = moveRotate;
      handle.onpointerup = endRotate;
      handle.onpointercancel = endRotate;
    }

    function moveRotate(event) {
      if (!rotation || rotation.handle !== event.currentTarget) return;
      event.preventDefault();
      const angle = Math.atan2(event.clientY - rotation.centerY, event.clientX - rotation.centerX) * 180 / Math.PI;
      let degrees = rotation.startRotation + angle - rotation.startAngle;
      if (event.shiftKey) degrees = Math.round(degrees / 15) * 15;
      degrees = ((degrees + 180) % 360 + 360) % 360 - 180;
      rotation.layer.dataset.rotation = degrees.toFixed(1);
      applyLayerStyle(rotation.layer);
      updateSelectedReadout();
    }

    function endRotate() {
      if (!rotation) return;
      rotation.handle.onpointermove = null;
      rotation.handle.onpointerup = null;
      rotation.handle.onpointercancel = null;
      const layer = rotation.layer;
      rotation = null;
      constrainLayer(layer);
      selectLayer(layer);
    }

    function addTextLayer() {
      if (type !== 'custom' && type !== 'banner' && type !== 'hardhat') return;
      pushHistory();
      const stickerText = type === 'hardhat';
      const layer = makeLayer({ key: `custom-${keyCounter++}`, kind: 'text', html: 'NEW TEXT', x: 50, y: stickerText ? 50 : clamp(45 + keyCounter * 5, 20, 82), fontSize: stickerText ? 18 : 64, font: fontSelect?.options[0].value, color: '#0d0e11', colorCode: '070', outlineColor: '#e6e9ee', outlineColorCode: '010', outlineThickness: 0, textShape: 'straight' });
      safeArea.appendChild(layer); bindLayers(); selectLayer(layer); editLayer(layer); renderLayerPanel();
    }

    function deleteSelected() {
      if (!selectedLayer) return;
      if (selectedLayer.dataset.lockedLayout === 'true') { toast('This template line cannot be deleted.'); return; }
      if (selectedLayer.dataset.key === 'artwork') {
        removeArtwork();
        toast('Uploaded artwork removed');
        return;
      }
      pushHistory(); const next = selectedLayer.previousElementSibling || selectedLayer.nextElementSibling;
      selectedLayer.remove(); selectLayer(next?.matches?.('[data-layer]') ? next : null); renderLayerPanel();
      toast('Selected item deleted');
    }

    function duplicateSelected() {
      if (!selectedLayer) return;
      if (selectedLayer.dataset.lockedLayout === 'true') { toast('This template line cannot be duplicated.'); return; }
      pushHistory(); const state = layerState(selectedLayer); state.key = `${state.kind}-${Date.now()}-${keyCounter++}`; state.x = clamp(state.x + 5, 5, 95); state.y = clamp(state.y + 5, 5, 95);
      const copy = makeLayer(state); safeArea.appendChild(copy); bindLayers(); selectLayer(copy); renderLayerPanel();
      toast('Selected item duplicated');
    }

    function centerSelected() {
      if (!selectedLayer) return; pushHistory(); selectedLayer.dataset.x = 50; applyLayerStyle(selectedLayer); selectLayer(selectedLayer);
    }

    function renderLayerPanel() {
      const list = one(lab, '[data-layers-list]'); if (!list) return;
      list.innerHTML = '';
      layers().slice().reverse().forEach((layer) => {
        if (layer.hidden) return;
        const li = document.createElement('li');
        const label = layer.dataset.kind === 'image' ? 'Uploaded artwork' : (plainLayerText(layer) || 'Blank text');
        li.classList.toggle('active', layer === selectedLayer);
        li.innerHTML = `<button type="button" class="layer-name">${escapeHTML(label.slice(0, 34))}</button><button type="button" class="layer-up" title="Move forward">↑</button><button type="button" class="layer-down" title="Move backward">↓</button>`;
        one(li, '.layer-name').onclick = () => selectLayer(layer);
        one(li, '.layer-up').onclick = () => moveLayer(layer, 1);
        one(li, '.layer-down').onclick = () => moveLayer(layer, -1);
        list.appendChild(li);
      });
      renderLayerChips();
    }

    function renderLayerChips() {
      const chips = one(lab, '[data-layer-chips]');
      if (!chips) return;
      chips.innerHTML = '';
      layers().filter((layer) => !layer.hidden).forEach((layer) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mobile-layer-chip';
        button.classList.toggle('active', layer === selectedLayer);
        const label = layer.dataset.kind === 'image' ? 'Artwork' : (plainLayerText(layer) || 'Blank text');
        button.textContent = label.slice(0, 24);
        button.setAttribute('aria-pressed', String(layer === selectedLayer));
        button.addEventListener('click', () => {
          selectLayer(layer);
          if (layer.dataset.kind === 'text') beginMobileTextEditing(selectedText, false);
        });
        chips.appendChild(button);
      });
    }

    function moveLayer(layer, direction) {
      const items = layers(); const index = items.indexOf(layer); const target = index + direction;
      if (target < 0 || target >= items.length) return;
      pushHistory();
      if (direction > 0) safeArea.insertBefore(layer, items[target].nextSibling);
      else safeArea.insertBefore(layer, items[target]);
      selectLayer(layer); renderLayerPanel();
    }

    function updateSwatchSelect() {
      if (!colorSelect) return;
      colorSelect.style.setProperty('--selected-color', colorSelect.value);
      colorSelect.style.backgroundImage = `linear-gradient(90deg, ${colorSelect.value} 0 42px, #fff 42px)`;
      const toolbarColorSwatch = one(lab, '[data-toolbar-color-swatch]');
      if (toolbarColorSwatch) toolbarColorSwatch.style.setProperty('--toolbar-color', colorSelect.value || '#0d0e11');
      if (!colorPicker || !colorTrigger || !colorMenu) return;
      const option = colorSelect.selectedOptions[0];
      const code = swatchName(option);
      one(colorTrigger, '[data-color-swatch]').style.setProperty('--swatch', colorSelect.value);
      one(colorTrigger, '[data-color-code]').textContent = code;
      all(colorMenu, '[data-color-value]').forEach((button) => {
        const active = button.dataset.colorValue === colorSelect.value;
        button.classList.toggle('active', active);
        button.setAttribute('aria-selected', String(active));
      });
    }

    function updateOutlineSwatchSelect() {
      if (!outlineColorSelect) return;
      outlineColorSelect.style.setProperty('--selected-color', outlineColorSelect.value);
      outlineColorSelect.style.backgroundImage = `linear-gradient(90deg, ${outlineColorSelect.value} 0 42px, #fff 42px)`;
      if (!outlineColorPicker || !outlineColorTrigger || !outlineColorMenu) return;
      const option = outlineColorSelect.selectedOptions[0];
      const code = swatchName(option);
      one(outlineColorTrigger, '[data-color-swatch]').style.setProperty('--swatch', outlineColorSelect.value);
      one(outlineColorTrigger, '[data-color-code]').textContent = code;
      all(outlineColorMenu, '[data-color-value]').forEach((button) => {
        const active = button.dataset.colorValue === outlineColorSelect.value;
        button.classList.toggle('active', active);
        button.setAttribute('aria-selected', String(active));
      });
    }

    function closeColorPicker() {
      if (!colorMenu || !colorTrigger) return;
      colorMenu.hidden = true;
      colorTrigger.setAttribute('aria-expanded', 'false');
    }

    function closeOutlineColorPicker() {
      if (!outlineColorMenu || !outlineColorTrigger) return;
      outlineColorMenu.hidden = true;
      outlineColorTrigger.setAttribute('aria-expanded', 'false');
    }

    function initColorPicker() {
      if (!colorSelect || colorSelect.dataset.enhanced === 'true') return;
      colorSelect.dataset.enhanced = 'true';
      colorSelect.classList.add('native-swatch-select');

      colorPicker = document.createElement('div');
      colorPicker.className = 'color-swatch-picker';
      colorTrigger = document.createElement('button');
      colorTrigger.type = 'button';
      colorTrigger.className = 'color-swatch-trigger';
      colorTrigger.setAttribute('aria-haspopup', 'listbox');
      colorTrigger.setAttribute('aria-expanded', 'false');
      colorTrigger.setAttribute('aria-label', 'Choose lettering color');

      const currentSwatch = document.createElement('span');
      currentSwatch.className = 'color-swatch';
      currentSwatch.dataset.colorSwatch = '';
      const currentCode = document.createElement('span');
      currentCode.dataset.colorCode = '';
      const caret = document.createElement('span');
      caret.className = 'color-swatch-caret';
      caret.textContent = '⌄';
      caret.setAttribute('aria-hidden', 'true');
      colorTrigger.append(currentSwatch, currentCode, caret);

      colorMenu = document.createElement('div');
      colorMenu.className = 'color-swatch-menu';
      colorMenu.setAttribute('role', 'listbox');
      colorMenu.setAttribute('aria-label', 'Lettering colors');
      colorMenu.hidden = true;

      Array.from(colorSelect.options).forEach((option) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'color-swatch-option';
        item.dataset.colorValue = option.value;
        item.setAttribute('role', 'option');

        const swatch = document.createElement('span');
        swatch.className = 'color-swatch';
        swatch.style.setProperty('--swatch', option.value);
        const code = document.createElement('span');
        code.textContent = swatchName(option);
        code.className = 'color-swatch-code';
        const check = document.createElement('span');
        check.className = 'color-swatch-check';
        check.textContent = '✓';
        check.setAttribute('aria-hidden', 'true');
        item.append(swatch, code, check);
        item.addEventListener('click', () => {
          colorSelect.value = option.value;
          colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
          closeColorPicker();
          colorTrigger.focus();
        });
        colorMenu.appendChild(item);
      });

      colorTrigger.addEventListener('click', () => {
        const opening = colorMenu.hidden;
        colorMenu.hidden = !opening;
        colorTrigger.setAttribute('aria-expanded', String(opening));
        if (opening) colorMenu.querySelector('.active')?.focus();
      });
      colorPicker.append(colorTrigger, colorMenu);
      colorSelect.insertAdjacentElement('afterend', colorPicker);
      document.addEventListener('pointerdown', (event) => {
        if (colorPicker && !colorPicker.contains(event.target)) closeColorPicker();
      });
    }

    function initOutlineColorPicker() {
      if (!outlineColorSelect || outlineColorSelect.dataset.enhanced === 'true') return;
      outlineColorSelect.dataset.enhanced = 'true';
      outlineColorSelect.classList.add('native-swatch-select');

      outlineColorPicker = document.createElement('div');
      outlineColorPicker.className = 'color-swatch-picker';
      outlineColorTrigger = document.createElement('button');
      outlineColorTrigger.type = 'button';
      outlineColorTrigger.className = 'color-swatch-trigger';
      outlineColorTrigger.setAttribute('aria-haspopup', 'listbox');
      outlineColorTrigger.setAttribute('aria-expanded', 'false');
      outlineColorTrigger.setAttribute('aria-label', 'Choose outline color');

      const currentSwatch = document.createElement('span');
      currentSwatch.className = 'color-swatch';
      currentSwatch.dataset.colorSwatch = '';
      const currentCode = document.createElement('span');
      currentCode.dataset.colorCode = '';
      const caret = document.createElement('span');
      caret.className = 'color-swatch-caret';
      caret.textContent = '⌄';
      caret.setAttribute('aria-hidden', 'true');
      outlineColorTrigger.append(currentSwatch, currentCode, caret);

      outlineColorMenu = document.createElement('div');
      outlineColorMenu.className = 'color-swatch-menu';
      outlineColorMenu.setAttribute('role', 'listbox');
      outlineColorMenu.setAttribute('aria-label', 'Outline colors');
      outlineColorMenu.hidden = true;

      Array.from(outlineColorSelect.options).forEach((option) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'color-swatch-option';
        item.dataset.colorValue = option.value;
        item.setAttribute('role', 'option');
        const swatch = document.createElement('span');
        swatch.className = 'color-swatch';
        swatch.style.setProperty('--swatch', option.value);
        const code = document.createElement('span');
        code.textContent = swatchName(option);
        code.className = 'color-swatch-code';
        const check = document.createElement('span');
        check.className = 'color-swatch-check';
        check.textContent = '✓';
        check.setAttribute('aria-hidden', 'true');
        item.append(swatch, code, check);
        item.addEventListener('click', () => {
          outlineColorSelect.value = option.value;
          outlineColorSelect.dispatchEvent(new Event('change', { bubbles: true }));
          closeOutlineColorPicker();
          outlineColorTrigger.focus();
        });
        outlineColorMenu.appendChild(item);
      });

      outlineColorTrigger.addEventListener('click', () => {
        const opening = outlineColorMenu.hidden;
        outlineColorMenu.hidden = !opening;
        outlineColorTrigger.setAttribute('aria-expanded', String(opening));
        if (opening) outlineColorMenu.querySelector('.active')?.focus();
      });
      outlineColorPicker.append(outlineColorTrigger, outlineColorMenu);
      outlineColorSelect.insertAdjacentElement('afterend', outlineColorPicker);
      document.addEventListener('pointerdown', (event) => {
        if (outlineColorPicker && !outlineColorPicker.contains(event.target)) closeOutlineColorPicker();
      });
    }

    function initBackgroundColorPicker() {
      if (!backgroundColorSelect || backgroundColorSelect.dataset.enhanced === 'true') return;
      backgroundColorSelect.dataset.enhanced = 'true';
      backgroundColorSelect.classList.add('native-swatch-select');

      const picker = document.createElement('div');
      picker.className = 'color-swatch-picker';
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'color-swatch-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', 'Choose background color');

      const currentSwatch = document.createElement('span');
      currentSwatch.className = 'color-swatch';
      currentSwatch.dataset.colorSwatch = '';
      const currentCode = document.createElement('span');
      currentCode.dataset.colorCode = '';
      const caret = document.createElement('span');
      caret.className = 'color-swatch-caret';
      caret.textContent = '⌄';
      caret.setAttribute('aria-hidden', 'true');
      trigger.append(currentSwatch, currentCode, caret);

      const menu = document.createElement('div');
      menu.className = 'color-swatch-menu';
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', 'Background colors');
      menu.hidden = true;

      const update = () => {
        const option = backgroundColorSelect.selectedOptions[0];
        const code = swatchName(option);
        currentSwatch.style.setProperty('--swatch', backgroundColorSelect.value);
        currentCode.textContent = code;
        all(menu, '[data-color-value]').forEach((button) => {
          const active = button.dataset.colorValue === backgroundColorSelect.value;
          button.classList.toggle('active', active);
          button.setAttribute('aria-selected', String(active));
        });
      };
      const close = () => {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      };

      Array.from(backgroundColorSelect.options).forEach((option) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'color-swatch-option';
        item.dataset.colorValue = option.value;
        item.setAttribute('role', 'option');
        const swatch = document.createElement('span');
        swatch.className = 'color-swatch';
        swatch.style.setProperty('--swatch', option.value);
        const code = document.createElement('span');
        code.textContent = swatchName(option);
        code.className = 'color-swatch-code';
        const check = document.createElement('span');
        check.className = 'color-swatch-check';
        check.textContent = '✓';
        check.setAttribute('aria-hidden', 'true');
        item.append(swatch, code, check);
        item.addEventListener('click', () => {
          backgroundColorSelect.value = option.value;
          backgroundColorSelect.dispatchEvent(new Event('change', { bubbles: true }));
          close();
          trigger.focus();
        });
        menu.appendChild(item);
      });

      trigger.addEventListener('click', () => {
        const opening = menu.hidden;
        menu.hidden = !opening;
        trigger.setAttribute('aria-expanded', String(opening));
        if (opening) menu.querySelector('.active')?.focus();
      });
      backgroundColorSelect.addEventListener('change', update);
      picker.append(trigger, menu);
      backgroundColorSelect.insertAdjacentElement('afterend', picker);
      document.addEventListener('pointerdown', (event) => {
        if (!picker.contains(event.target)) close();
      });
      update();
    }

    function initBannerBackgroundGrid() {
      if (type !== 'banner' || !backgroundColorSelect) return;
      const grid = one(lab, '[data-banner-background-grid]');
      if (!grid) return;
      const buttons = all(grid, '[data-background-choice]');
      const update = () => {
        buttons.forEach((button) => {
          const active = button.dataset.backgroundChoice === backgroundColorSelect.value;
          button.classList.toggle('active', active);
          button.setAttribute('aria-selected', String(active));
        });
      };
      buttons.forEach((button) => button.addEventListener('click', () => {
        backgroundColorSelect.value = button.dataset.backgroundChoice;
        backgroundColorSelect.dispatchEvent(new Event('change', { bubbles: true }));
        update();
      }));
      backgroundColorSelect.addEventListener('change', update);
      update();
    }

    function visualSelectKind(select) {
      if (select === fontSelect) return 'font';
      if (select === signalSelect) return 'signal';
      if (select === sizeSelect) return 'size';
      if (select === orientationSelect) return 'orientation';
      if (select === materialSelect) return 'material';
      if (select === mountingSelect) return 'mounting';
      return 'choice';
    }

    function initVisualSelect(select) {
      if (!select || select.dataset.visualEnhanced === 'true') return;
      select.dataset.visualEnhanced = 'true';
      select.classList.add('visual-choice-select');
      const kind = visualSelectKind(select);
      const group = document.createElement('div');
      group.className = `visual-choice-group visual-choice-group--${kind}`;
      group.dataset.forSelect = '';
      group.setAttribute('role', 'listbox');
      group.setAttribute('aria-label', select.closest('label')?.childNodes?.[0]?.textContent?.trim() || 'Choose an option');
      Array.from(select.options).forEach((option) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'visual-choice';
        button.dataset.value = option.value;
        button.setAttribute('role', 'option');
        if (kind === 'font') {
          const sample = document.createElement('span');
          sample.className = 'visual-choice-sample';
          sample.textContent = 'Aa';
          sample.style.fontFamily = option.value;
          const label = document.createElement('span');
          label.textContent = option.textContent.trim();
          button.append(sample, label);
        } else {
          button.textContent = option.textContent.trim();
        }
        button.addEventListener('click', () => {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          refreshVisualSelect(select);
        });
        group.appendChild(button);
      });
      select.insertAdjacentElement('afterend', group);
      refreshVisualSelect(select);
    }

    function refreshVisualSelect(select) {
      if (!select) return;
      const group = select.nextElementSibling?.matches?.('.visual-choice-group') ? select.nextElementSibling : null;
      if (!group) return;
      all(group, '[data-value]').forEach((button) => {
        const option = Array.from(select.options).find((item) => item.value === button.dataset.value);
        const active = button.dataset.value === select.value;
        button.classList.toggle('active', active);
        button.setAttribute('aria-selected', String(active));
        button.disabled = Boolean(option?.disabled);
      });
    }

    function mobileViewportHeight() {
      return Math.max(320, window.visualViewport?.height || window.innerHeight);
    }

    function updateMobileViewport() {
      const height = mobileViewportHeight();
      lab.style.setProperty('--mobile-viewport-height', `${height}px`);
      lab.style.setProperty('--mobile-viewport-top', `${window.visualViewport?.offsetTop || 0}px`);
      if (!mobileQuery.matches) return;
      const focused = lab.contains(document.activeElement) && (document.activeElement.matches?.('input, textarea') || document.activeElement.isContentEditable);
      lab.classList.toggle('keyboard-open', Boolean(focused && height < window.innerHeight * 0.88));
    }

    function beginMobileTextEditing(input, focus = true) {
      if (!mobileQuery.matches || !input) return;
      lab.classList.add('mobile-editing');
      if (focus) setTimeout(() => { input.focus(); input.select?.(); input.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
    }

    function finishMobileTextEditing() {
      const editing = one(lab, '[contenteditable=true]');
      if (editing) editing.blur();
      if (lab.contains(document.activeElement)) document.activeElement.blur?.();
      lab.classList.remove('mobile-editing', 'keyboard-open');
      selectLayer(null);
    }

    function handleArtwork(file) {
      if (!file) return;
      const limit = type === 'lease' ? 5 : 25;
      if (file.size > limit * 1024 * 1024) {
        const message = `Artwork must be ${limit} MB or smaller.`;
        if (cartMessage) cartMessage.textContent = message;
        toast(message);
        artworkInput.value = '';
        return;
      }
      const layer = layerByKey('artwork'); if (!layer) return;
      pushHistory(); layer.dataset.fileName = file.name;
      const image = one(layer, 'img');
      if (file.type.startsWith('image/') || /\.(png|jpe?g|webp|svg)$/i.test(file.name)) {
        if (layer.dataset.objectUrl) URL.revokeObjectURL(layer.dataset.objectUrl);
        const objectUrl = URL.createObjectURL(file);
        layer.dataset.objectUrl = objectUrl;
        image.onload = () => {
          if (layer.dataset.objectUrl !== objectUrl) return;
          image.hidden = false;
          layer.hidden = false;
          layer.classList.remove('file-only');
          layer.removeAttribute('data-file-label');
          if (type === 'lease') {
            const operatorLayer = layerByKey('operator');
            if (operatorLayer) operatorLayer.hidden = true;
          }
          if (cartMessage) cartMessage.textContent = '';
          if (type === 'banner' || type === 'hardhat') fitArtworkLayer(layer);
          else { applyLayerStyle(layer); constrainLayer(layer); }
          selectLayer(layer); renderLayerPanel();
          toast(type === 'lease' ? 'Logo added to the sign' : 'Artwork added to the sign');
        };
        image.onerror = () => {
          if (layer.dataset.objectUrl === objectUrl) {
            URL.revokeObjectURL(objectUrl);
            delete layer.dataset.objectUrl;
          }
          image.removeAttribute('src');
          layer.hidden = true;
          if (type === 'lease') {
            const operatorLayer = layerByKey('operator');
            if (operatorLayer) operatorLayer.hidden = false;
          }
          if (removeArtworkButton) removeArtworkButton.hidden = true;
          const message = 'This image could not be previewed. Please choose a PNG, JPG, WebP or SVG file.';
          if (cartMessage) cartMessage.textContent = message;
          toast(message);
          artworkInput.value = '';
        };
        image.src = objectUrl;
      } else {
        image.hidden = true;
        layer.hidden = false;
        layer.classList.add('file-only');
        layer.setAttribute('data-file-label', file.name);
        applyLayerStyle(layer); constrainLayer(layer); selectLayer(layer);
      }
      if (removeArtworkButton) removeArtworkButton.hidden = false;
      renderLayerPanel();
    }

    function removeArtwork() {
      const layer = layerByKey('artwork'); if (!layer) return;
      pushHistory();
      if (layer.dataset.objectUrl) {
        URL.revokeObjectURL(layer.dataset.objectUrl);
        delete layer.dataset.objectUrl;
      }
      const image = one(layer, 'img');
      if (image) { image.onload = null; image.onerror = null; image.removeAttribute('src'); }
      layer.hidden = true; layer.classList.remove('file-only');
      if (artworkInput) artworkInput.value = '';
      if (type === 'lease') { const operatorLayer = layerByKey('operator'); if (operatorLayer) operatorLayer.hidden = false; }
      if (removeArtworkButton) removeArtworkButton.hidden = true;
      selectLayer(type === 'lease' ? layerByKey('operator') : null); renderLayerPanel();
    }

    function setPanel(panelName) {
      all(lab, '[data-panel]').forEach((panel) => panel.hidden = panel.dataset.panel !== panelName);
      all(lab, '[data-tab]').forEach((tab) => tab.classList.toggle('active', tab.dataset.tab === panelName));
      if (mobileQuery.matches) one(lab, '.lab-controls')?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function revealCustomControl(selector, openPicker = false) {
      setPanel('design');
      let layer = selectedLayer;
      if (!layer || layer.dataset.kind !== 'text') layer = layers().find((item) => item.dataset.kind === 'text' && !item.hidden);
      if (layer) selectLayer(layer);
      const target = one(lab, selector);
      if (!target) return;
      target.classList.add('tool-focus');
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => target.classList.remove('tool-focus'), 1400);
      if (openPicker) setTimeout(() => target.querySelector('.color-swatch-trigger')?.click(), 260);
    }

    function runCustomTool(action) {
      if (action === 'center') {
        if (!selectedLayer) selectLayer(layers().find((item) => item.dataset.kind === 'text' && !item.hidden) || layers().find((item) => !item.hidden));
        centerSelected();
        return;
      }
      if (action === 'text') { openTextEditor(); return; }
      if (action === 'background') revealCustomControl('[data-background-row]', true);
      else if (action === 'color') revealCustomControl('[data-color-row]', true);
      else if (action === 'font') revealCustomControl('[data-font-row]');
      else if (action === 'shape') revealCustomControl('[data-text-shape-row]');
      else if (action === 'outline') revealCustomControl('[data-outline-color-row]');
    }

    const savedDesignKey = `fsp-design:${location.host}:${lab.dataset.productUrl || type}`;
    const legacySavedDesignKey = `fsp-custom-sign:${location.host}:${lab.dataset.productUrl || 'custom-sign'}`;

    function saveDesign() {
      try {
        let needsArtwork = false;
        const savedLayers = capture().map((item) => {
          if (item.kind !== 'image' || item.hidden || !item.src.startsWith('blob:')) return item;
          needsArtwork = true;
          return { ...item, src: '', hidden: true };
        });
        localStorage.setItem(savedDesignKey, JSON.stringify({
          version: 2,
          type,
          savedAt: new Date().toISOString(),
          size: sizeSelect?.value || '',
          material: materialSelect?.value || '',
          packQuantity: packQuantitySelect?.value || '',
          mounting: mountingSelect?.value || '',
          orientation: orientationSelect?.value || '',
          signal: signalSelect?.value || '',
          background: backgroundColorSelect?.value || '#ffffff',
          signQuantity: one(lab, '[data-sign-quantity]')?.value || '1',
          riderText: one(lab, '[data-rider-text]')?.value || '',
          riderColor: one(lab, '[data-rider-color]')?.value || '',
          riderQuantity: one(lab, '[data-rider-quantity]')?.value || '0',
          frameQuantity: one(lab, '[data-frame-quantity]')?.value || '0',
          postLength: one(lab, '[data-post-length]')?.value || '',
          postQuantity: one(lab, '[data-post-quantity]')?.value || '0',
          needsArtwork,
          layers: savedLayers
        }));
        toast(needsArtwork ? 'Design saved. Reattach uploaded artwork when you return.' : 'Design saved on this device');
      } catch (_) {
        toast('This browser could not save the design');
      }
    }

    function loadDesign() {
      try {
        const stored = localStorage.getItem(savedDesignKey) || (type === 'custom' ? localStorage.getItem(legacySavedDesignKey) : '');
        const saved = JSON.parse(stored || 'null');
        if (!saved || !Array.isArray(saved.layers)) { toast('No saved design was found on this device'); return; }
        pushHistory();
        if (sizeSelect && saved.size && Array.from(sizeSelect.options).some((option) => option.value === saved.size)) sizeSelect.value = saved.size;
        if (materialSelect && saved.material && Array.from(materialSelect.options).some((option) => option.value === saved.material)) materialSelect.value = saved.material;
        if (packQuantitySelect && saved.packQuantity && Array.from(packQuantitySelect.options).some((option) => option.value === saved.packQuantity)) packQuantitySelect.value = saved.packQuantity;
        if (mountingSelect && saved.mounting && Array.from(mountingSelect.options).some((option) => option.value === saved.mounting)) mountingSelect.value = saved.mounting;
        if (orientationSelect && saved.orientation && Array.from(orientationSelect.options).some((option) => option.value === saved.orientation)) orientationSelect.value = saved.orientation;
        if (signalSelect && saved.signal && Array.from(signalSelect.options).some((option) => option.value === saved.signal)) signalSelect.value = saved.signal;
        if (backgroundColorSelect && saved.background) backgroundColorSelect.value = saved.background;
        const restoreValue = (selector, value) => { const field = one(lab, selector); if (field && value !== undefined && value !== null && value !== '') field.value = String(value); };
        restoreValue('[data-sign-quantity]', saved.signQuantity);
        restoreValue('[data-rider-text]', saved.riderText);
        restoreValue('[data-rider-color]', saved.riderColor);
        restoreValue('[data-rider-quantity]', saved.riderQuantity);
        restoreValue('[data-frame-quantity]', saved.frameQuantity);
        restoreValue('[data-post-length]', saved.postLength);
        restoreValue('[data-post-quantity]', saved.postQuantity);
        [orientationSelect, signalSelect].filter(Boolean).forEach((field) => field.dispatchEvent(new Event('change', { bubbles: true })));
        restore(saved.layers);
        all(lab, '[data-bind]').forEach((input) => {
          const layer = layerByKey(input.dataset.bind);
          if (layer) input.value = plainLayerText(layer);
        });
        [sizeSelect, materialSelect, packQuantitySelect, mountingSelect, backgroundColorSelect, one(lab, '[data-rider-color]'), one(lab, '[data-post-length]')].filter(Boolean).forEach((field) => field.dispatchEvent(new Event('change', { bubbles: true })));
        applyBackgroundColor();
        applyDimensions();
        updateSwatchSelect();
        toast(saved.needsArtwork ? 'Saved design loaded. Please reattach its artwork.' : 'Saved design loaded');
      } catch (_) {
        toast('The saved design could not be loaded');
      }
    }

    function renderMountingHoles() {
      const overlay = one(lab, '[data-mounting-holes]'); if (!overlay) return;
      overlay.innerHTML = '';
      if (type === 'banner') {
        const dims = currentDimensions();
        sign.dataset.mounting = 'Hemmed edges with grommets';
        overlay.style.setProperty('--hole-size', `${Math.max(5, (0.38 / dims.width) * sign.clientWidth)}px`);
        const edgeInset = 0.75;
        const addGrommet = (x, y) => {
          const grommet = document.createElement('i');
          grommet.className = 'hole banner-grommet';
          grommet.style.left = `${(x / dims.width) * 100}%`;
          grommet.style.top = `${(y / dims.height) * 100}%`;
          overlay.appendChild(grommet);
        };
        const columns = Math.max(2, Math.ceil((dims.width - edgeInset * 2) / 24) + 1);
        const rows = Math.max(2, Math.ceil((dims.height - edgeInset * 2) / 24) + 1);
        for (let index = 0; index < columns; index += 1) {
          const x = edgeInset + ((dims.width - edgeInset * 2) * index) / Math.max(1, columns - 1);
          addGrommet(x, edgeInset); addGrommet(x, dims.height - edgeInset);
        }
        for (let index = 1; index < rows - 1; index += 1) {
          const y = edgeInset + ((dims.height - edgeInset * 2) * index) / Math.max(1, rows - 1);
          addGrommet(edgeInset, y); addGrommet(dims.width - edgeInset, y);
        }
        return;
      }
      const mounting = mountingSelect?.value || 'No mounting holes';
      sign.dataset.mounting = mounting;
      if (mounting === 'No mounting holes' || materialSelect?.value === 'Vinyl Decal') return;
      const dims = currentDimensions();
      overlay.style.setProperty('--hole-x', `${(0.5 / dims.width) * 100}%`);
      overlay.style.setProperty('--hole-y', `${(0.5 / dims.height) * 100}%`);
      overlay.style.setProperty('--hole-size', `${Math.max(5, (0.25 / dims.width) * sign.clientWidth)}px`);
      const names = mounting === 'Four corner holes' ? ['tl', 'tr', 'bl', 'br'] : ['tc', 'bc'];
      names.forEach((name) => { const i = document.createElement('i'); i.className = `hole ${name}`; overlay.appendChild(i); });
    }

    function selectedOptions() {
      const mounting = mountingSelect?.value || 'No mounting holes';
      if (type === 'lease') return [mounting];
      if (type === 'rider') {
        const color = normalize(layerByKey('rider')?.dataset.color) === normalize('#0d0e11') ? 'Black' : 'Red';
        return [color, 'No mounting holes'];
      }
      if (type === 'banner') return [sizeSelect?.value];
      if (type === 'hardhat') return [sizeSelect?.value, materialSelect?.value, packQuantitySelect?.value];
      return [sizeSelect?.value, materialSelect?.value, mounting];
    }

    function findVariant(item, values) {
      if (!item?.variants?.length) return null;
      return item.variants.find((variant) => values.every((value, index) => normalize(variant.options?.[index] || variant[`option${index + 1}`]) === normalize(value))) || item.variants[0];
    }

    function activeMainVariant() { return findVariant(product, selectedOptions()); }

    function updatePrice() {
      if (!product) return;
      if (materialSelect && mountingSelect) {
        const vinyl = materialSelect.value === 'Vinyl Decal';
        if (vinyl && mountingSelect.value !== 'No mounting holes') mountingSelect.value = 'No mounting holes';
        all(mountingSelect, 'option').forEach((option) => { option.disabled = vinyl && option.value !== 'No mounting holes'; });
      }
      renderMountingHoles();
      const signQty = Math.max(1, Number(one(lab, '[data-sign-quantity]')?.value || 1));
      let total = (activeMainVariant()?.price || product.price || 0) * signQty;
      if (type === 'lease') {
        total += Number(one(lab, '[data-rider-quantity]')?.value || 0) * (findVariant(accessories.rider, [one(lab, '[data-rider-color]')?.value || 'Red', 'No mounting holes'])?.price || 0);
        total += Number(one(lab, '[data-frame-quantity]')?.value || 0) * (accessories.frame?.variants?.[0]?.price || 3800);
      } else if (type !== 'banner' && type !== 'hardhat' && type !== 'rider') {
        total += Number(one(lab, '[data-post-quantity]')?.value || 0) * (findVariant(accessories.post, [one(lab, '[data-post-length]')?.value || "6′"])?.price || 0);
      }
      const totalEl = one(lab, '[data-design-total]'); if (totalEl) totalEl.textContent = money(total);
      const mainPrice = document.querySelector('[data-product-price]'); if (mainPrice) mainPrice.textContent = money(activeMainVariant()?.price || product.price || 0);
    }

    function designProperties() {
      const dims = currentDimensions();
      const props = {
        'Design type': type === 'lease' ? 'Lease / Well Sign' : type === 'rider' ? 'Lease Sign Rider' : type === 'safety' ? `${signalSelect?.value || 'DANGER'} Safety Sign` : type === 'pipeline' ? 'Pipeline Warning Sign' : type === 'banner' ? 'Custom Banner' : type === 'hardhat' ? 'Custom Hard Hat Sticker' : 'Custom Sign',
        'Size': type === 'banner' || type === 'hardhat' ? dims.label : `${dims.width}″ × ${dims.height}″`,
        'Material': type === 'lease' || type === 'rider' ? '.040 Aluminum' : type === 'banner' ? '13 oz outdoor vinyl banner' : materialSelect?.value,
        'Proof required': 'Yes — contact customer for approval before production'
      };
      if (type === 'custom' && product?.handle && product.handle !== 'custom-sign-designer') props['Product template'] = product.title;
      if (type === 'banner') props['Finishing'] = 'Hemmed edges with grommets';
      else if (type === 'hardhat') {
        props['Sticker shape'] = stickerShape === 'rounded-square' ? 'Rounded square' : stickerShape === 'die-cut' ? 'Custom die-cut' : stickerShape.charAt(0).toUpperCase() + stickerShape.slice(1);
        props['Stickers per set'] = packQuantitySelect?.value || '25 stickers';
        props['Production cut path'] = stickerShape === 'die-cut' ? 'Custom contour — create CutContour from approved artwork' : `${stickerShape} CutContour`;
        props['Application guidance'] = 'Keep at least 1/2 inch from helmet edge; verify helmet manufacturer compatibility';
      } else props['Mounting'] = 'No mounting holes';
      if (backgroundColorSelect) {
        props['Background color'] = swatchName(backgroundColorSelect.selectedOptions[0]);
        props['_Background color'] = backgroundColorSelect.value || '#ffffff';
      }
      if (type === 'pipeline') props['Orientation'] = orientationSelect?.value || 'Landscape';
      layers().filter((layer) => layer.dataset.kind === 'text' && !layer.hidden).forEach((layer, index) => {
        const label = ({ rider: 'Rider wording', operator: 'Operator / Company', lease: 'Lease / Well', misc: 'Misc. information', location: 'Section / Township / Range', county: 'County / State', phone: type === 'pipeline' ? 'Emergency phone' : 'Phone', pipeline: 'Pipeline wording', 'before-digging': 'Before digging line', 'emergency-call': 'Emergency call line', company: 'Company name', facility: 'Facility / Station name', 'product-stored': 'Product stored', restricted: 'Access message', direction: 'Directional arrow', distance: 'Distance', 'emergency-heading': 'Emergency heading', 'operator-phone': '24-hour phone', 'decal-line-1': 'Replacement decal line 1', 'decal-line-2': 'Replacement decal line 2', line1: 'Line 1', line2: 'Line 2', line3: 'Line 3' })[layer.dataset.key] || `Custom text ${index + 1}`;
        const value = plainLayerText(layer).trim(); if (value) props[label] = value;
        if (type === 'rider' && layer.dataset.key === 'rider') props['Letter color'] = normalize(layer.dataset.color) === normalize('#0d0e11') ? 'Black' : 'Red';
        const outlineThickness = Number(layer.dataset.outlineThickness || 0);
        if ((type === 'custom' || type === 'banner' || type === 'hardhat') && value && outlineThickness > 0) props[`${label} outline`] = `${outlineThickness} pt · Oracal ${layer.dataset.outlineColorCode || '010'}`;
        if ((type === 'custom' || type === 'banner' || type === 'hardhat') && value && (layer.dataset.textShape || 'straight') !== 'straight') props[`${label} shape`] = layer.dataset.textShape;
      });
      if (type === 'safety') props['Safety header'] = signalSelect?.value || 'DANGER';
      if (artworkInput?.files?.[0]) props['Artwork filename'] = artworkInput.files[0].name;
      const compact = layers().filter((layer) => !layer.hidden).map((layer) => {
        const s = layerState(layer);
        return [
          s.key,
          Number(Number(s.x).toFixed(2)),
          Number(Number(s.y).toFixed(2)),
          Math.round(s.kind === 'image' ? s.size : s.fontSize),
          s.colorCode || '',
          s.font || '',
          s.color || '',
          s.outlineColorCode || '',
          s.outlineColor || '',
          Number(Number(s.outlineThickness || 0).toFixed(1)),
          Number(Number(s.rotation || 0).toFixed(1)),
          s.textShape || 'straight'
        ];
      });
      props['_Design layout'] = JSON.stringify(compact);
      props['_Design snapshot'] = JSON.stringify(layers().filter((layer) => !layer.hidden).map((layer, order) => {
        const s = layerState(layer);
        return {
          key: s.key,
          kind: s.kind,
          order,
          text: s.html,
          x: Number(Number(s.x).toFixed(2)),
          y: Number(Number(s.y).toFixed(2)),
          rotation: Number(Number(s.rotation || 0).toFixed(1)),
          fontSize: Math.round(s.fontSize || 64),
          size: Number(Number(s.size || 35).toFixed(1)),
          font: s.font,
          color: s.color,
          colorCode: s.colorCode,
          outlineColor: s.outlineColor,
          outlineColorCode: s.outlineColorCode,
          outlineThickness: Number(Number(s.outlineThickness || 0).toFixed(1)),
          textShape: s.textShape || 'straight'
        };
      }));
      return props;
    }

    async function postFormData(formData) {
      const response = await fetch((window.fspRoutes?.root || '/') + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.description || body.message || 'Shopify could not add this item.');
      return body;
    }

    async function postItems(items) {
      if (!items.length) return;
      const response = await fetch((window.fspRoutes?.root || '/') + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.description || body.message || 'Shopify could not add an accessory.');
    }

    async function addToCart() {
      const variant = activeMainVariant();
      if (!variant) { cartMessage.textContent = 'This product needs to be imported before the designer can add it to cart.'; return; }
      addButton.disabled = true; addButton.textContent = 'Adding design…'; cartMessage.textContent = '';
      try {
        const formData = new FormData();
        formData.append('id', variant.id); formData.append('quantity', Math.max(1, Number(one(lab, '[data-sign-quantity]')?.value || 1)));
        Object.entries(designProperties()).forEach(([key, value]) => formData.append(`properties[${key}]`, value || ''));
        if (artworkInput?.files?.[0]) formData.append(artworkInput.name || 'properties[Customer artwork]', artworkInput.files[0]);
        await postFormData(formData);
        const extraItems = [];
        if (type === 'lease') {
          const riderQty = Math.max(0, Number(one(lab, '[data-rider-quantity]')?.value || 0));
          const frameQty = Math.max(0, Number(one(lab, '[data-frame-quantity]')?.value || 0));
          const riderColor = one(lab, '[data-rider-color]')?.value || 'Red';
          const riderVariant = findVariant(accessories.rider, [riderColor, 'No mounting holes']);
          if (riderQty && riderVariant) extraItems.push({ id: riderVariant.id, quantity: riderQty, properties: { 'Rider wording': one(lab, '[data-rider-text]')?.value || 'NO TRESPASSING', 'Letter color': riderColor, 'Proof required': 'Yes' } });
          if (frameQty && accessories.frame?.variants?.[0]) extraItems.push({ id: accessories.frame.variants[0].id, quantity: frameQty });
        } else if (type !== 'banner' && type !== 'hardhat' && type !== 'rider') {
          const postQty = Math.max(0, Number(one(lab, '[data-post-quantity]')?.value || 0));
          const postLength = one(lab, '[data-post-length]')?.value || "6′";
          const postVariant = findVariant(accessories.post, [postLength]);
          if (postQty && postVariant) extraItems.push({ id: postVariant.id, quantity: postQty });
        }
        await postItems(extraItems);
        const cart = await fetch((window.fspRoutes?.root || '/') + 'cart.js').then((response) => response.json());
        document.querySelectorAll('[data-cart-count]').forEach((count) => count.textContent = cart.item_count);
        toast('Your design was added to the cart');
        window.location.href = window.fspRoutes?.cart || '/cart';
      } catch (error) {
        cartMessage.textContent = error.message || 'Something went wrong. Please try again.';
        addButton.disabled = false; addButton.textContent = 'Add design to cart';
      }
    }

    function openLab() {
      lab.hidden = false;
      document.body.classList.add('lab-open');
      window.scrollTo(0, 0);
      requestAnimationFrame(() => { updateMobileViewport(); applyDimensions(); fitAll(); });
    }
    function closeLab() { finishMobileTextEditing(); lab.hidden = true; document.body.classList.remove('lab-open'); }

    function openTextEditor() {
      setPanel('design');
      const editable = selectedLayer?.dataset.kind === 'text'
        ? selectedLayer
        : layers().find((layer) => layer.dataset.kind === 'text' && !layer.hidden);
      if (!editable) return;
      selectLayer(editable);
      if (mobileQuery.matches && selectedText) beginMobileTextEditing(selectedText);
    }

    document.querySelectorAll('[data-open-design-lab]').forEach((button) => button.addEventListener('click', openLab));
    one(lab, '[data-close-design-lab]')?.addEventListener('click', closeLab);
    one(lab, '[data-undo]')?.addEventListener('click', undo);
    one(lab, '[data-redo]')?.addEventListener('click', redo);
    all(lab, '[data-add-text]').forEach((button) => button.addEventListener('click', addTextLayer));
    all(lab, '[data-trigger-upload]').forEach((button) => { if (artworkInput) button.addEventListener('click', () => artworkInput.click()); else button.hidden = true; });
    artworkInput?.addEventListener('change', () => handleArtwork(artworkInput.files?.[0]));
    removeArtworkButton?.addEventListener('click', removeArtwork);
    one(lab, '[data-delete]')?.addEventListener('click', deleteSelected);
    one(lab, '[data-duplicate]')?.addEventListener('click', duplicateSelected);
    one(lab, '[data-center]')?.addEventListener('click', centerSelected);
    one(lab, '[data-save-design]')?.addEventListener('click', saveDesign);
    one(lab, '[data-load-design]')?.addEventListener('click', loadDesign);
    all(lab, '[data-custom-tool]').forEach((button) => button.addEventListener('click', () => runCustomTool(button.dataset.customTool)));
    all(lab, '[data-text-shape]').forEach((button) => button.addEventListener('click', () => {
      if (!selectedLayer || selectedLayer.dataset.kind !== 'text') return;
      pushHistory();
      selectedLayer.dataset.textShape = button.dataset.textShape || 'straight';
      applyTextShape(selectedLayer);
      fitLayer(selectedLayer);
      constrainLayer(selectedLayer);
      selectLayer(selectedLayer);
      toast(`Text shape: ${button.textContent.trim()}`);
    }));
    one(lab, '[data-fit-artwork]')?.addEventListener('click', () => {
      if (!selectedLayer || selectedLayer.dataset.kind !== 'image') return;
      pushHistory();
      fitArtworkLayer(selectedLayer, true);
    });
    addButton?.addEventListener('click', addToCart);
    one(lab, '[data-done-editing]')?.addEventListener('click', finishMobileTextEditing);
    stage.addEventListener('pointerdown', (event) => {
      if (!selectedLayer || event.target.closest('[data-layer], .resize-handle, .rotate-handle, .custom-sign-action')) return;
      const editing = one(selectedLayer, '[contenteditable=true]');
      if (editing) editing.blur();
      lastTap = { time: 0, layer: null };
      selectLayer(null);
    });

    all(lab, '[data-bind]').forEach((input) => {
      input.addEventListener('focus', () => {
        const layer = layerByKey(input.dataset.bind);
        if (layer) { selectLayer(layer); pushHistory(); }
        if (mobileQuery.matches) setTimeout(() => input.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
      });
      input.addEventListener('input', () => { const layer = layerByKey(input.dataset.bind); const content = one(layer, '[data-layer-content]'); if (content) { content.textContent = input.value; fitLayer(layer); constrainLayer(layer); renderLayerPanel(); } });
    });
    selectedText?.addEventListener('focus', () => { pushHistory(); beginMobileTextEditing(selectedText, false); });
    selectedText?.addEventListener('input', () => {
      if (!selectedLayer) return;
      const content = one(selectedLayer, '[data-layer-content]');
      if (content) {
        selectedLayer.dataset.plainText = selectedText.value;
        content.textContent = selectedText.value;
        const boundInput = one(lab, `[data-bind="${CSS.escape(selectedLayer.dataset.key)}"]`);
        if (boundInput) boundInput.value = selectedText.value;
        applyTextShape(selectedLayer);
        fitLayer(selectedLayer); constrainLayer(selectedLayer); renderLayerPanel();
      }
    });
    fontSelect?.addEventListener('change', () => {
      refreshVisualSelect(fontSelect);
      if (!selectedLayer) return;
      pushHistory();
      const layer = selectedLayer;
      layer.dataset.font = normalizeFontStack(fontSelect.value);
      applyLayerStyle(layer);
      constrainLayer(layer);
      const fontReady = document.fonts?.load?.(`900 32px ${layer.dataset.font}`);
      fontReady?.then(() => {
        if (!layer.isConnected) return;
        applyLayerStyle(layer);
        constrainLayer(layer);
        renderLayerPanel();
      });
    });
    colorSelect?.addEventListener('change', () => { if (!selectedLayer) return; pushHistory(); selectedLayer.dataset.color = colorSelect.value; selectedLayer.dataset.colorCode = colorSelect.selectedOptions[0]?.dataset.code || ''; applyLayerStyle(selectedLayer); updateSwatchSelect(); if (type === 'rider') updatePrice(); });
    backgroundColorSelect?.addEventListener('change', applyBackgroundColor);
    outlineColorSelect?.addEventListener('change', () => {
      if (!selectedLayer || selectedLayer.dataset.kind !== 'text') return;
      pushHistory();
      selectedLayer.dataset.outlineColor = outlineColorSelect.value;
      selectedLayer.dataset.outlineColorCode = outlineColorSelect.selectedOptions[0]?.dataset.code || '';
      applyLayerStyle(selectedLayer);
      updateOutlineSwatchSelect();
    });
    outlineThicknessInput?.addEventListener('focus', () => {
      if (selectedLayer?.dataset.kind === 'text') pushHistory();
    });
    outlineThicknessInput?.addEventListener('input', () => {
      if (!selectedLayer || selectedLayer.dataset.kind !== 'text') return;
      const thickness = clamp(Number(outlineThicknessInput.value || 0), 0, 12);
      selectedLayer.dataset.outlineThickness = String(thickness);
      if (outlineThicknessValue) outlineThicknessValue.textContent = `${thickness} pt`;
      applyLayerStyle(selectedLayer);
      constrainLayer(selectedLayer);
      updateSelectedReadout();
    });
    signalSelect?.addEventListener('change', () => {
      signalHeader.className = `safety-header signal-${signalSelect.value.toLowerCase()}`;
      signalHeader.dataset.signalValue = signalSelect.value;
      sign.dataset.signal = signalSelect.value.toLowerCase();
      refreshVisualSelect(signalSelect);
    });
    all(lab, '[data-tab]').forEach((tab) => tab.addEventListener('click', () => setPanel(tab.dataset.tab)));
    all(lab, '[data-tool]').forEach((button) => button.addEventListener('click', () => {
      if (button.dataset.tool === 'design') openTextEditor();
      else setPanel(button.dataset.tool);
    }));

    const layerPanels = all(lab, '[data-layers-panel]');
    all(lab, '[data-toggle-layers]').forEach((button) => button.addEventListener('click', () => { layerPanels.forEach((panel) => panel.hidden = !panel.hidden); renderLayerPanel(); }));
    all(lab, '[data-close-layers]').forEach((button) => button.addEventListener('click', () => layerPanels.forEach((panel) => panel.hidden = true)));
    orientationSelect?.addEventListener('change', () => { refreshVisualSelect(orientationSelect); applyPipelineOrientationLayout(); applyDimensions(); fitAll(); updatePrice(); });
    [sizeSelect, materialSelect, packQuantitySelect, mountingSelect, one(lab, '[data-sign-quantity]'), one(lab, '[data-rider-quantity]'), one(lab, '[data-frame-quantity]'), one(lab, '[data-post-quantity]'), one(lab, '[data-post-length]'), one(lab, '[data-rider-color]')].filter(Boolean).forEach((field) => field.addEventListener('change', () => { refreshVisualSelect(field); applyDimensions(); updatePrice(); }));

    initColorPicker();
    initOutlineColorPicker();
    if (type === 'banner') initBannerBackgroundGrid();
    else initBackgroundColorPicker();
    [signalSelect, orientationSelect, sizeSelect, materialSelect, packQuantitySelect, mountingSelect, fontSelect, one(lab, '[data-rider-color]'), one(lab, '[data-post-length]')].filter(Boolean).forEach(initVisualSelect);
    updateMobileViewport();
    window.visualViewport?.addEventListener('resize', updateMobileViewport);
    window.visualViewport?.addEventListener('scroll', updateMobileViewport);
    mobileQuery.addEventListener?.('change', () => { updateMobileViewport(); applyDimensions(); });
    bindLayers(); applyBackgroundColor(); applyDimensions(); renderLayerPanel(); updateSwatchSelect(); updateOutlineSwatchSelect(); updateHistoryButtons();
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        layers().forEach(applyLayerStyle);
        fitAll();
        if (selectedLayer) selectLayer(selectedLayer);
      });
    }
    let resizeFrame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => { applyDimensions(); fitAll(); renderMountingHoles(); });
    });
    observer.observe(stage);
    if (new URLSearchParams(window.location.search).get('design') === '1') {
      openLab();
      requestAnimationFrame(() => requestAnimationFrame(openLab));
      window.addEventListener('pageshow', () => { if (lab.hidden) openLab(); }, { once: true });
    }
  }
})();
